import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DashboardWrapper } from 'src/shared';
import { UserSelectors } from 'src/reducers/user';
import * as api from 'src/services/api';
import styled from 'styled-components';

const Container = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  padding: 24px;
`;
const Title = styled.h1`
  font-size: 1.5rem;
  margin-bottom: 16px;
`;
const Section = styled.section`
  margin-bottom: 24px;
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;
const SectionTitle = styled.h2`
  font-size: 1.1rem;
  margin-bottom: 12px;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
`;
const Card = styled.div`
  padding: 12px;
  background: #f1f5f9;
  border-radius: 8px;
`;
const Label = styled.div`
  font-size: 12px;
  color: #64748b;
`;
const Value = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
`;

export default function AdminHealth() {
  const navigate = useNavigate();
  const { profile, isAllowedAdmin } = UserSelectors();
  const [data, setData] = useState<{
    backend: {
      cpu_percent: number | null;
      memory_percent: number | null;
      memory_mb: number | null;
      api_requests: Record<string, number>;
    };
    logins: {
      success: Record<string, number>;
      failed: Record<string, number>;
    };
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!profile) {
      navigate('/login');
      return;
    }
    if (isAllowedAdmin !== true) {
      navigate('/');
      return;
    }
    let cancelled = false;
    api.getAdminHealth()
      .then((res) => {
        if (!cancelled && res?.data) setData(res.data);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.data?.message || 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [profile, isAllowedAdmin, navigate]);

  if (!profile) return null;
  if (isAllowedAdmin !== true) return null;

  const windows = ['1m', '10m', '30m', '1h', '1d', '1w'];

  return (
    <DashboardWrapper>
      <Container>
        <Title>Admin – System health</Title>
        <p style={{ marginBottom: 16 }}>
            <Link to="/admin/users" style={{ marginRight: 16 }}>Users</Link>
            <Link to="/admin/health" style={{ marginRight: 16, fontWeight: 600 }}>Health</Link>
            <Link to="/">Home</Link>
        </p>
        {error && <p style={{ color: 'red' }}>{error}</p>}
        {loading && <p>Loading...</p>}
        {data && (
          <>
            <Section>
              <SectionTitle>Backend</SectionTitle>
              <Grid>
                {data.backend.cpu_percent != null && (
                  <Card>
                    <Label>CPU %</Label>
                    <Value>{data.backend.cpu_percent}%</Value>
                  </Card>
                )}
                {data.backend.memory_percent != null && (
                  <Card>
                    <Label>Memory %</Label>
                    <Value>{data.backend.memory_percent}%</Value>
                  </Card>
                )}
                {data.backend.memory_mb != null && (
                  <Card>
                    <Label>Memory (MB)</Label>
                    <Value>{data.backend.memory_mb}</Value>
                  </Card>
                )}
              </Grid>
              <SectionTitle style={{ marginTop: 16 }}>API requests</SectionTitle>
              <Grid>
                {windows.map((w) => (
                  <Card key={w}>
                    <Label>{w}</Label>
                    <Value>{data.backend.api_requests[w] ?? 0}</Value>
                  </Card>
                ))}
              </Grid>
            </Section>
            <Section>
              <SectionTitle>Logins – success</SectionTitle>
              <Grid>
                {windows.map((w) => (
                  <Card key={w}>
                    <Label>{w}</Label>
                    <Value>{data.logins.success[w] ?? 0}</Value>
                  </Card>
                ))}
              </Grid>
            </Section>
            <Section>
              <SectionTitle>Logins – failed</SectionTitle>
              <Grid>
                {windows.map((w) => (
                  <Card key={w}>
                    <Label>{w}</Label>
                    <Value>{data.logins.failed[w] ?? 0}</Value>
                  </Card>
                ))}
              </Grid>
            </Section>
          </>
        )}
      </Container>
    </DashboardWrapper>
  );
}
